import { Tabs, type TabsProps } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import './VendorLeadStepContact.scss';

import { VendorLeadStepAddressTab } from 'src/components/vendors/steps/Contact/VendorLeadStepAddressTab';
import { VendorLeadStepContactTab } from 'src/components/vendors/steps/Contact/VendorLeadStepContactTab';
import { VendorLeadStepLayout } from 'src/components/vendors/steps/VendorLeadStepLayout';
import { VendorsContext } from 'src/pages/VendorsPage/VendorsPage.context';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';

const VendorLeadStepContactSubSteps: Partial<Record<string, string>> = {
  [ProductSpecificationFieldGroup.Contacts]: ProductSpecificationFieldGroup.ShippingAddress,
  [ProductSpecificationFieldGroup.ShippingAddress]: ProductSpecificationFieldGroup.BillingAddress,
};

type VendorLeadStepContactProps = {
  vendorProductLeadId: string;
};

export function VendorLeadStepContact({ vendorProductLeadId }: Readonly<VendorLeadStepContactProps>) {
  const [currentTab, setCurrentTab] = useState<string>(ProductSpecificationFieldGroup.Contacts);

  const { setVendorStepGuideKey, setLastVendorStep } = useContext(VendorsContext);

  const handleSubmit = useCallback(() => {
    const newSubStep = VendorLeadStepContactSubSteps[currentTab];

    const moveToNextStep = newSubStep === undefined;

    if (!moveToNextStep) {
      setCurrentTab(newSubStep);
    }

    // Go to next step if empty
    return moveToNextStep;
  }, [currentTab]);

  const tabs: Exclude<TabsProps['items'], undefined> = useMemo(
    () => [
      {
        key: ProductSpecificationFieldGroup.Contacts,
        label: 'Contact',
        children: <VendorLeadStepContactTab vendorProductLeadId={vendorProductLeadId} onSubmit={handleSubmit} />,
      },
      {
        key: ProductSpecificationFieldGroup.ShippingAddress,
        label: 'Shipping Address',
        children: (
          <VendorLeadStepAddressTab
            vendorProductLeadId={vendorProductLeadId}
            addressFieldName="shippingAddress"
            onSubmit={handleSubmit}
          />
        ),
      },
      {
        key: ProductSpecificationFieldGroup.BillingAddress,
        label: 'Billing Address',
        children: (
          <VendorLeadStepAddressTab
            vendorProductLeadId={vendorProductLeadId}
            addressFieldName="billingAddress"
            onSubmit={handleSubmit}
          />
        ),
      },
    ],
    [vendorProductLeadId, handleSubmit]
  );

  const handleTabChange = useCallback(
    (newActiveKey: string) => {
      setVendorStepGuideKey(
        (
          [
            ProductSpecificationFieldGroup.Contacts,
            ProductSpecificationFieldGroup.ShippingAddress,
            ProductSpecificationFieldGroup.BillingAddress,
          ] as string[]
        ).includes(newActiveKey)
          ? newActiveKey
          : null
      );

      setCurrentTab(newActiveKey as ProductSpecificationFieldGroup);
    },
    [setVendorStepGuideKey]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    setVendorStepGuideKey(
      (
        [
          ProductSpecificationFieldGroup.Contacts,
          ProductSpecificationFieldGroup.ShippingAddress,
          ProductSpecificationFieldGroup.BillingAddress,
        ] as string[]
      ).includes(currentTab)
        ? currentTab
        : null
    );

    setLastVendorStep(currentTab === ProductSpecificationFieldGroup.BillingAddress);
  }, [currentTab]);

  return (
    <VendorLeadStepLayout>
      <div className="vendor-lead-step-contact__container">
        <Tabs
          items={tabs}
          activeKey={currentTab}
          animated={false}
          destroyInactiveTabPane={false}
          onChange={handleTabChange}
        />
      </div>
    </VendorLeadStepLayout>
  );
}
